<script setup>
/*
 * Kampagnetilbud — baade de haandindtastede og de automatiske.
 *
 * ÉT KORT, TO KILDER. Forskellen er hvor tallene kommer fra, ikke
 * hvordan de ser ud, saa der skal ikke vaere to komponenter der driver
 * fra hinanden.
 *
 * FORBEHOLDET AFHÆNGER AF KILDEN. En haandindtastet pris er ikke hentet
 * fra et feed og ikke efterproevet af os, og det skal staa. En
 * automatisk er maalt, og saa staar der noget andet.
 */
import { computed } from 'vue';

const props = defineProps({ data: { type: Object, required: true } });

const tilbud = computed(() => props.data.tilbud || []);
const automatisk = computed(() => props.data.automatisk === true);

function kr(v) {
    const n = Number(v);
    if (isNaN(n)) return '';
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(n);
}

// Besparelsen i procent, regnet mod dét vi selv har maalt.
function procent(t) {
    if (!t.voresPris || !t.pris) return null;
    const p = Math.round((1 - Number(t.pris) / Number(t.voresPris)) * 100);
    return p >= 5 ? p : null;
}
</script>

<template>
    <p v-if="!tilbud.length" class="tom">
        {{ automatisk ? 'Ingen tilbud lever op til kravene lige nu.' : 'Ingen tilbud er åbne lige nu.' }}
    </p>

    <div v-else>
        <div class="kampagnegitter">
            <article v-for="(t, i) in tilbud" :key="i" class="kort">
                <a v-if="t.produktUrl" :href="t.produktUrl" class="kort__billede">
                    <picture v-if="t.billede">
                        <source :srcset="`${t.billede}?w=320&fmt=webp`" type="image/webp" />
                        <img :src="`${t.billede}?w=320`" :alt="t.navn" width="160" height="160" loading="lazy" decoding="async" />
                    </picture>
                    <span v-else class="kort__intet" aria-hidden="true"></span>
                    <span v-if="procent(t)" class="kort__fald">−{{ procent(t) }}<span class="kort__tegn">%</span></span>
                </a>

                <div class="kort__krop">
                    <span v-if="t.kategori" class="kort__kat">{{ t.kategori }}</span>
                    <h3 class="kort__navn">{{ t.navn }}</h3>
                    <p class="kort__butik">{{ t.butik }}</p>

                    <p class="kort__pris">{{ kr(t.pris) }}</p>

                    <p v-if="t.forskel !== null && t.forskel > 0" class="kort__spar">
                        {{ kr(t.forskel) }} under den laveste pris vi har målt
                    </p>
                    <p v-else-if="t.voresPris" class="kort__maalt">
                        Vi har målt {{ kr(t.voresPris) }} som laveste
                    </p>

                    <p v-if="t.note" class="kort__note">{{ t.note }}</p>

                    <a v-if="t.link" :href="t.link" class="btn btn-sm btn-pricecompare-primary" rel="nofollow sponsored" target="_blank">
                        Se hos {{ t.butik }}
                    </a>
                </div>
            </article>
        </div>

        <p v-if="automatisk" class="forbehold">
            Tilbuddene her er valgt automatisk ud fra vores egne daglige prismålinger, og sorteret
            efter hvor meget de faktisk er under varens laveste målte pris — ikke efter hvad butikken
            selv oplyser. Priserne kan ændre sig, og lagerstatus skifter hurtigt på kampagnedage.
        </p>
        <p v-else class="forbehold">
            Priserne i denne liste er <strong>indtastet i hånden</strong> og ikke hentet automatisk
            fra butikken. Vi har ikke efterprøvet dem mod vores egen prishistorik. Tjek prisen hos
            butikken før du køber.
        </p>
    </div>
</template>

<style scoped>
.tom, .forbehold { color: var(--pc-text-secondary); font-size: .85rem; margin-bottom: 0; }
.forbehold { margin-top: 1rem; max-width: 72ch; }

.kampagnegitter { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fill, minmax(13rem, 1fr)); }

.kort {
    border: 1px solid var(--pc-border); border-radius: var(--pc-radius-lg);
    background: var(--pc-bg-secondary); overflow: hidden;
    display: flex; flex-direction: column;
}
.kort__billede { position: relative; display: block; background: #fff; }
.kort__billede img { width: 100%; height: 9rem; object-fit: contain; display: block; padding: .5rem; }
.kort__intet { display: block; width: 100%; height: 9rem; background: var(--pc-bg-tertiary); }
.kort__fald {
    position: absolute; top: .4rem; right: .4rem;
    width: 2.6rem; height: 2.6rem; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--pc-price); color: var(--pc-on-accent, #0A0C10);
    font-weight: 800; font-size: .95rem;
}
.kort__tegn { font-size: .6rem; align-self: flex-start; margin-top: .45rem; }

.kort__krop { padding: .75rem .9rem 1rem; display: flex; flex-direction: column; gap: .2rem; flex: 1; }
.kort__kat { color: var(--pc-text-secondary); font-size: .68rem; text-transform: uppercase; letter-spacing: .06em; }
.kort__navn { font-size: .92rem; line-height: 1.3; margin: 0; color: var(--pc-text-primary); }
.kort__butik { color: var(--pc-text-secondary); font-size: .8rem; margin: 0; }
.kort__pris { font-size: 1.3rem; font-weight: 700; color: var(--pc-price); margin: .3rem 0 0; }
.kort__spar { color: var(--pc-success); font-size: .78rem; margin: 0; }
.kort__maalt, .kort__note { color: var(--pc-text-secondary); font-size: .78rem; margin: 0; }
.kort__krop .btn { margin-top: auto; align-self: flex-start; }
</style>
